<template>
  <kst-container class="kst-item-select">
    <ValidationObserver ref="formDetails">
      <kst-form submit
        :cancelUrl="cancelUrl"
        :errors="errors"
        :failure="failure"
        :failureInit="failureInit"
        :hide-body="!!formWarnings"
        :hide-footer="!!formWarnings"
        :loading="isLoading"
        :title="title"
        :warnings="formWarnings"
        @submit="handleFormSubmit"
      >
        <kst-section search-mode class="pb-0">
          <Search
            :searchData="searchData"
            @reset="handleSearcReset"
            @submit="handleSearchSubmit"
          />
        </kst-section>

        <kst-section search-result-mode select-one-help>
          <List 
            :failureList="failureList" 
            :items="items" 
            :isListLoading="isListLoading" 
            :userData="userData"
          />
        </kst-section>
      </kst-form>
    </ValidationObserver>
  </kst-container>
</template>

<script>
import AxiosMixin from "@/mixins/AxiosMixin.js";
import InputPageMixin from "@/mixins/InputPageMixin.js";
import LoadingMixin from "@/mixins/LoadingMixin.js";
import RouterMixin from "@/mixins/RouterMixin.js";
import SnackbarMixin from "@/mixins/SnackbarMixin.js";
import Info from "../ItemInfo.js";
import Model from "../ItemModel.js";
import RouterModel from "@/router/RouterModel.js";
import Services from "../ItemServices.js";

import Search from "./ItemSelect_Search";
import List from "./ItemSelect_List";
import Router from "../ItemRouter.js";
import PurchaseRouter from "@/components/Purchase/PurchaseRouter.js";

export default {
  mixins: [AxiosMixin, InputPageMixin, LoadingMixin, RouterMixin, SnackbarMixin],
  components: {
    Search,
    List
  },
  data: () => ({
    Model: Model,
    searchData: {},
    items: [],
    userData: {},
    purchaseData: null,
    isLoading: false,
    isListLoading: true,
    cancelUrl: Router.Path.List,
    saveLoading: null,
    // support debounce
    loadCounter: 0,
    // error
    errors: null,
    failure: null,
    failureInit: null,
    failureList: null,
    formWarnings: null
  }),
  computed: {
    title() {
      return RouterModel.getNavSubTitle(Model.Module, Model.ModuleType.Select);
    }
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      const purchaseItemId = this.getUrlParam(this.$kst.Enum.Param.PurchaseItemID);
      const purchaseId = this.getUrlParam(this.$kst.Enum.Param.PurchaseID);
      const itemName = this.getUrlParam(this.$kst.Enum.Param.Name);
      this.formWarnings = null;

      if (!purchaseItemId || !purchaseId || !itemName) {
        this.formWarnings = [Info.Message.Select_ParamIncomplete];
        return;
      }

      this.userData = {
        selected: null
      };

      this.purchaseData = {
        purchaseId: purchaseId,
        purchaseItemId: purchaseItemId,
        itemName: itemName
      };

      this.initSearch(itemName);
      this.initLoad();
    },
    initSearch(itemName) {
      this.searchData = {
        Search: itemName
      };
    },
    initLoad() {
      this.isLoading = true;
      this.failureInit = null;
      this.formWarnings = null;

      this.loadPurchaseItem();
    },
    initLoad_End() {
      this.isLoading = false;
    },

    loadPurchaseItem() {
      const config = Services.getPurchaseItemDetails(this.purchaseData.purchaseItemId);
      this.runAxios(config, null, this.loadPurchaseItem_Success, this.loadPurchaseItem_Error);
    },
    loadPurchaseItem_Success(data) {
      if (data.DispatchID !== Model.NewItem.DispatchID) {
        this.formWarnings = [Info.Message.New_RegisteredNewItem];
      }

      this.initLoad_End();
      this.loadCounter++;
      this.loadList(this.loadCounter, this.searchData);
    },
    loadPurchaseItem_Error(message) {
      this.failureInit = message;
      this.initLoad_End();
    },

    loadList(counter, searchData) {
      this.failureList = null;
      this.isListLoading = true;
      this.items = [];
      
      let options = {};
      if (searchData) {
        options.name = this.$kst.Out.getString(searchData.Search);
        options.field = Model.Search.Fields;
      }

      const config = Services.getDispatchList(options);
      this.runAxios(config, counter, this.loadList_Success, this.loadList_Error);
    },
    loadList_End() {
      this.isListLoading = false;
    },
    loadList_Success(data, counter) {
      // handle repeated execution on slow network to show last request
      if (counter !== this.loadCounter) {
        return;
      }

      this.items = data;
      this.loadList_End();
    },
    loadList_Error(message) {
      this.failureList = message;
      this.loadList_End();
    },

    handleSearchSubmit() {
      this.loadCounter++;
      this.loadList(this.loadCounter, this.searchData);
    },
    handleSearcReset() {
      this.init();
    },

    handleFormSubmit() {
      if (this.userData.selected === null) {
        this.notifyError_SelectOne();
        return;
      }

      this.handleFormSubmit_Success();
    },
    handleFormSubmit_Success() {
      const item = this.userData.selected;

      const data = {
        DispatchID: item.DispatchID,
        Name: item.FullName,
        ID: this.purchaseData.purchaseItemId
      };

      this.saveData(data);
    },

    saveData(data) {
      this.errors = null;
      this.failure = null;
      this.saveLoading = this.openLoading();

      const config = Services.postPurchaseItem(data);
      this.runAxios(config, null,
        this.saveData_Success,
        this.saveData_Error,
        this.saveData_Invalid
      );
    },
    saveData_End() {
      this.closeLoading(this.saveLoading);
      this.saveLoading = null;
    },
    saveData_Success() {
      this.saveData_End();
      this.routeLeaveEnabled = false;

      this.notifyFormSuccess(() => {
        const urlParams = this.$kst.Route.getParams({
          [this.$kst.Enum.Param.ID]: this.purchaseData.purchaseId
        });
        this.redirectTo(PurchaseRouter.Path.Details + urlParams);
      });
    },
    saveData_Error(message) {
      this.failure = message;
      this.saveData_End();
    },
    saveData_Invalid(message) {
      this.errors = [message];
      this.saveData_End();
      this.notifyFormError();
    }
  }
}
</script>